'use client';

import { useEffect, useState, type SyntheticEvent } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import { useKey } from 'react-use';
import styled, { css } from 'styled-components';
import { IconShare, IconShareCommon } from '@/app/common/icons/libraryIcons';
import { trackSocialShare } from '@/app/components/hotel-detail/trackInteractionEvents';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation';
import ShareFallback, { adaptAppSharingUrl } from './ShareFallback';
import customCss from './css/customCss';
import { useUrlSearchParams } from './UrlSearchParamsProvider';
import { clientAppLog } from '../search/logging/clientAppLog';

type shareButtonProps = {
  hotelName: string;
  variant?: string;
  showText?: boolean;
} & React.HTMLAttributes<HTMLElement>;

// TODO Review on 1sdp, removed some code to make it work
const StyledMenuButton = styled.button`
  align-items: center;
  justify-content: center;
  cursor: pointer;
  display: flex;
  fill: currentColor;
  font-size: ${(p) => p.theme.kallisto.font.size['175']};
  border: none;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer02};
  padding: 0;
  width: 36px;
  height: 36px;

  & svg,
  & [data-library-icon] {
    width: 20px;
    height: 20px;
  }

  @media ${customCss.breakpoints.sm} {
    background-color: unset;
  }
`;

// `showText` variant: icon and label inline as an underlined text link, for the evo desktop top
// bar where share sits next to "Back to search" instead of floating over the gallery.
const StyledShareLink = styled.button`
  ${({ theme }) => theme.kallisto.mixins.font('Body')};
  align-items: center;
  background-color: unset;
  border: none;
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  cursor: pointer;
  display: flex;
  fill: currentColor;
  font-family: inherit;
  gap: 8px;
  padding: 0;
  text-decoration: underline;

  & svg,
  & [data-library-icon] {
    width: 20px;
    height: 20px;
  }
`;

export default function ShareButton(props: shareButtonProps) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { hotelName, variant = 'default', showText = false, ...rest } = props;
  const {
    commonData: { isApp },
  } = usePageWrapperData();
  const [isExpanded, setIsExpanded] = useState(false);
  const { sendCuriosityClickEvent } = useCuriosity();
  const [supportsWebShare, setSupportsWebShare] = useState<undefined | boolean>(undefined);
  const hotelTitle = hotelName;
  const { accommodationOnly } = useUrlSearchParams();
  const { t } = useTranslation();
  const userAgent = typeof navigator !== 'undefined' ? (navigator?.userAgent?.toLowerCase() ?? '') : '';
  const isAndroidPhone = userAgent.match(/android/i) && userAgent.match(/mobile/i);

  useEffect(() => {
    const webShare = navigator.share;
    if (webShare) {
      setSupportsWebShare(true);
    } else {
      setSupportsWebShare(false);
    }
  }, [supportsWebShare]);

  const handleClick = (e: SyntheticEvent) => {
    e.stopPropagation();

    sendCuriosityClickEvent({
      event_name: 'click_share',
      event_value: isExpanded ? 'to_collapse_it' : 'to_expand_it',
    });

    setIsExpanded((expanded) => !expanded);

    if (supportsWebShare) {
      e.preventDefault();
      const shareUrl = adaptAppSharingUrl({ accommodationOnly, isApp });
      navigator
        .share?.({
          title: hotelTitle,
          url: shareUrl,
        })
        .then(() => {
          trackSocialShare('-');
        })
        .catch((error: Error) => {
          if (error.name === 'AbortError') {
            return;
          }
          clientAppLog({
            logMessage: `Error in ShareButton: ${error.message}`,
            codeSourceName: 'ShareButton',
            level: 'WARN',
            url: window?.location?.href,
          });
        });
    }
  };

  // The round variant paints the icon itself so it stays readable on top of the gallery; the
  // labelled one lets it inherit the link colour.
  const iconCss = showText
    ? css`
        pointer-events: none;
      `
    : css`
        pointer-events: none;
        color: ${(p) => p.theme.kallisto.color.icon.static.iconPrimary};
      `;

  const icon = isAndroidPhone ? <IconShare css={iconCss} /> : <IconShareCommon css={iconCss} />;

  // Desktop Chrome and Firefox have no `navigator.share`, so the fallback panel is the common
  // desktop path rather than the exception. Give it real popover behaviour: dismiss on outside
  // click and on Escape, and expose the open state so AT announces it as a disclosure.
  const hasFallbackPanel = supportsWebShare === false;
  const isPanelOpen = hasFallbackPanel && isExpanded;
  const containerRef = useOnclickOutside(() => setIsExpanded(false), { disabled: !isPanelOpen });
  useKey('Escape', () => setIsExpanded(false));
  const disclosureProps = hasFallbackPanel ? { 'aria-haspopup': true, 'aria-expanded': isPanelOpen } : {};

  return (
    <div
      ref={containerRef}
      css={`
        display: flex;
        flex-direction: column;
        gap: 8px;
        justify-content: center;
        align-items: center;
        position: relative;
      `}
    >
      <div
        {...rest}
        css={css`
          display: flex;
          gap: 8px;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          width: ${showText ? 'auto' : '36px'};
          border-radius: 36px;
        `}
      >
        {showText ? (
          <StyledShareLink onClick={handleClick} {...disclosureProps}>
            {icon}
            {t('vacation.common.share')}
          </StyledShareLink>
        ) : (
          <StyledMenuButton onClick={handleClick} aria-label={t('vacation.common.share')} {...disclosureProps}>
            {icon}
          </StyledMenuButton>
        )}
      </div>
      {isPanelOpen ? (
        <div
          css={css`
            position: absolute;
            top: ${showText ? 'calc(100% + 4px)' : '40px'};
            ${showText ? 'right: 0;' : ''}
            background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundPage};
            border: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
            border-radius: 8px;
            box-shadow: ${({ theme }) => theme.kallisto.shadow.mediumDownShadow};
            display: flex;
            flex-direction: column;
            gap: 8px;
            padding: 8px;
            justify-content: center;
          `}
        >
          <ShareFallback title={hotelTitle} />
        </div>
      ) : undefined}
    </div>
  );
}
